import { createReducer, on } from "@ngrx/store";
import { resourceStructureDefaultValue, userResourcesInitialState, UserResourcesState } from "../initial-states/user-resources.states";
import { clearUserResources, initializeUserResources, loadUserResources, loadUserResourcesFailed, loadUserResourcesSuccess, resetUserResources, updateUserResources, upsertUserResources } from "../actions/user-resources.actions";
import { UserResourceInterfaceType, UserResourceType, UserResources } from "src/app/models/user-resources.model";
import { SITETEMPLATENAMES } from "src/app/data/siteTemplateId.data";


export const UserResourcesReducer = createReducer(userResourcesInitialState,

	on(initializeUserResources,
		(state, { userIds }) => {
			userIds.forEach(userId => {
				// Validate if the m365user is presented on state
				const userStateIndex = state.findIndex(user => user.m365UserId === userId);
				// If not, we need to add the user to the state
				if (userStateIndex === -1) {
					const newUserResourceState: UserResourcesState = {
						m365UserId: userId,
						userResources: { ...resourceStructureDefaultValue }
					}
					state.push(newUserResourceState);
				}
			});
			return state;
		}
	),

	// The reducer runs when a dispatch 'loadUserResources' was triggered
	// The only thing we need to do here is set loading to true
	on(loadUserResources,
		(state, { m365UserId, resourceType, skipToken, loadedFrom }) => {

			// Validate if the m365user is presented on state
			const userStateIndex = state.findIndex(user => user.m365UserId === m365UserId);

			// If not, we need to add the user to the state
			if (userStateIndex === -1) {
				const newUserResourceState: UserResourcesState = {
					m365UserId: m365UserId,
					userResources: { ...resourceStructureDefaultValue }
				}

				newUserResourceState.userResources[resourceType] = { ...newUserResourceState.userResources[resourceType], loading: true, value: { ...newUserResourceState.userResources[resourceType].value, skipToken, loadedFrom } };
				state.push(newUserResourceState);
			}
			else {
				state[userStateIndex].userResources[resourceType] = { ...state[userStateIndex].userResources[resourceType], loading: true, value: { ...state[userStateIndex].userResources[resourceType].value, skipToken, loadedFrom } };
			}

			return state;
		}
	),
	// The reducer runs when a dispatch 'loadUserResourcesSuccess' was triggered
	// Here we need to set the loading to false, set the value of 'loadedOnce' and add the value of the resources for the specific type
	on(loadUserResourcesSuccess,
		(state, { m365UserId, resourceType, userResources, loadedFrom, expandedProperties }) => {
			if (loadedFrom) {
				userResources.loadedFrom = loadedFrom;
			}

			// Validate if the m365user is presented on state
			const userStateIndex = state.findIndex(user => user.m365UserId === m365UserId);

			if (userStateIndex !== -1) {
				const updatedValue = UpdateResources(resourceType, state[userStateIndex].userResources[resourceType].value, userResources);

				/** Merge the expand properties that were used in this load with any previously stored ones */
				const previouslyExpanded = state[userStateIndex].userResources[resourceType].value.expandedProperties ?? [];
				updatedValue.expandedProperties = expandedProperties
					? [...new Set([...previouslyExpanded, ...expandedProperties])]
					: previouslyExpanded.length > 0 ? previouslyExpanded : undefined;

				state[userStateIndex].userResources[resourceType] = {
					...state[userStateIndex].userResources[resourceType],
					loading: false,
					loadedOnce: true,
					value: updatedValue,
				};
			}

			return [...state];
		}
	),


	// The reducer runs when a dispatch 'loadUserResourcesFailed' was triggered
	// Here we need to set the state to the initial one, set the value of 'loadedOnce' and add the value of the error action result into the store
	on(loadUserResourcesFailed,
		(state, { m365UserId, resourceType, error }) => {
			// Validate if the m365user is presented on state
			const userStateIndex = state.findIndex(user => user.m365UserId === m365UserId);

			if (userStateIndex !== -1) {
				state[userStateIndex].userResources[resourceType] = {
					...state[userStateIndex].userResources[resourceType],
					loading: false,
					loadedOnce: true,
					value: { resources: [], skipToken: undefined, total: undefined, loadedFrom: undefined },
					error: error
				};
			}

			return [...state];
		}
	),

	// The reducer runs when a dispatch 'resetUserResources' was triggered
	// Here we need to reset the value of the user resources to the default one
	on(resetUserResources,
		() => {
			return [
				...userResourcesInitialState
			];
		}
	),

	// The reducer runs when a dispatch 'updateUserResource' was triggered
	// Here we need to set the loading to false, set the value of 'loadedOnce' and add the value of the resources for the specific type
	on(updateUserResources, (state, { m365UserId, resourceType, userResources }) => {
		const userStateIndex = state.findIndex(user => user.m365UserId === m365UserId);
		if (userStateIndex !== -1) {
			state[userStateIndex].userResources[resourceType] = {
				...state[userStateIndex].userResources[resourceType],
				value: {
					...state[userStateIndex].userResources[resourceType].value,
					resources: UpdatedResources(
						resourceType,
						state[userStateIndex].userResources[resourceType].value.resources,
						userResources
					)
				}
			};
		}
		return state;
	}),

	// The reducer runs when a dispatch 'upsertUserResources' was triggered
	on(upsertUserResources, (state, { m365UserId, resourceType, userResources, loadedFrom }) => {
		const userStateIndex = state.findIndex(user => user.m365UserId === m365UserId);
		if (userStateIndex !== -1) {
			state[userStateIndex].userResources[resourceType] = {
				...state[userStateIndex].userResources[resourceType],
				value: {
					...state[userStateIndex].userResources[resourceType].value,
					resources: UpsertResources(
						resourceType,
						state[userStateIndex].userResources[resourceType].value.resources,
						userResources
					),
					loadedFrom
				}
			};
		}
		return state;
	}),

	// The reducer runs when a dispatch 'updateUserResource' was triggered
	// Here we need to set the loading to false, set the value of 'loadedOnce' and add the value of the resources for the specific type
	on(clearUserResources, (state, { m365UserId, resourceType }) => {
		const userStateIndex = state.findIndex(user => user.m365UserId === m365UserId);
		if (userStateIndex !== -1) {
			state[userStateIndex].userResources[resourceType] = {
				value: { resources: [], skipToken: undefined, total: undefined, loadedFrom: undefined, expandedProperties: undefined },
				loading: false,
				loadedOnce: false,
				lazyLoading: false,
				error: undefined
			};
		}
		return state;
	}),
)


function UpdatedResources(resourceType: UserResourceType, currentListOfResources: UserResourceInterfaceType, resourcesToUpdate: UserResourceInterfaceType) {
	switch (resourceType) {
		case 'sites':
		case 'hubsites':
			resourcesToUpdate.forEach(r => {
				const index = currentListOfResources.findIndex((site) => site.url === r.url);
				if (index !== -1) {
					currentListOfResources[index] = r;
				}
			})
			break;
		default:
			break;
	}

	return currentListOfResources
}

function UpsertResources(resourceType: UserResourceType, currentListOfResources: UserResourceInterfaceType, resourcesToUpdate: UserResourceInterfaceType) {
	switch (resourceType) {
		case 'sites':
		case 'hubsites':
			resourcesToUpdate.forEach(r => {
				if (r.url) {
					r.urlParsed = new URL(r.url)?.pathname !== '/' ? ".." + new URL(r.url)?.pathname : r.url;
				}

				if (r.template) {
					const templateCodeName = SITETEMPLATENAMES.find(t => t.id?.toLocaleUpperCase() === r.template?.toLocaleUpperCase());
					r.spSiteTemplateName = templateCodeName?.name ?? r.template[0].toUpperCase() + r.template.toLocaleLowerCase().substring(1, r.template.length);
				}
				const index = currentListOfResources.findIndex((site) => site.url === r.url);
				if (index !== -1) {
					currentListOfResources[index] = r;
				} else {
					currentListOfResources.push(r);
				}
			})
			break;
		default:
			break;
	}

	return currentListOfResources
}

// Function to update the list of resources
export function UpdateResources(resourceType: UserResourceType, currentResources: UserResources, newValues: UserResources) {

	const currentResourcesClone = JSON.parse(JSON.stringify(currentResources))

	switch (resourceType) {
		case 'sites':
		case 'hubsites':
			newValues.resources.forEach(x => x.isHubSite = x.hubSite.isHubSite);

			// Filter out non-hubsites if resourceType is 'hubsites'
			if (resourceType === 'hubsites') {
				newValues.resources = newValues.resources.filter(x => x.isHubSite);
			}
			break;
		default:
			break;
	}

	newValues.resources.forEach(x => {
		if (x.url) {
			x.urlParsed = new URL(x.url)?.pathname !== '/' ? ".." + new URL(x.url)?.pathname : x.url;
		}

		if (x.template) {
			const templateCodeName = SITETEMPLATENAMES.find(t => t.id?.toLocaleUpperCase() === x.template?.toLocaleUpperCase());
			x.spSiteTemplateName = templateCodeName?.name ?? x.template[0].toUpperCase() + x.template.toLocaleLowerCase().substring(1, x.template.length);
		}
	});

	// If this is the first iteration, update total and resources
	if (currentResourcesClone.resources.length === 0) {
		currentResourcesClone.total = newValues.total;
		currentResourcesClone.resources = newValues.resources;
	} else {
		currentResourcesClone.resources = [
			...currentResourcesClone.resources,
			...newValues.resources
		].filter((resource, index, self) =>
			index === self.findIndex((r) => r.url === resource.url)
		);
	}

	currentResourcesClone.skipToken = newValues.skipToken;
	currentResourcesClone.loadedFrom = newValues.loadedFrom;

	return currentResourcesClone;
}
